import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Ticket, Plus, Pencil, Trash2, Crown, Lock } from "lucide-react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const EMPTY = { code: "", discount_type: "percent", discount_value: "", expires_at: "", max_uses: "", active: true };

export default function PromoCodes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [codes, setCodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const isPro = status && status.is_pro;

  const load = async () => {
    try {
      const s = await api.get("/billing/status");
      setStatus(s.data);
      if (s.data.is_pro) {
        const r = await api.get("/promo-codes");
        setCodes(r.data);
      }
    } catch (e) { toast.error("Errore nel caricamento"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code, discount_type: c.discount_type, discount_value: String(c.discount_value),
      expires_at: c.expires_at || "", max_uses: c.max_uses != null ? String(c.max_uses) : "", active: c.active,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim()) return toast.error("Inserisci il codice");
    if (!(Number(form.discount_value) > 0)) return toast.error("Lo sconto deve essere maggiore di zero");
    setSaving(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      scope: "quote",
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      expires_at: form.expires_at || null,
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      active: form.active,
    };
    try {
      if (editing) await api.put(`/promo-codes/${editing.id}`, payload);
      else await api.post("/promo-codes", payload);
      toast.success(editing ? "Codice aggiornato" : "Codice creato");
      setOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    setSaving(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare il codice "${c.code}"?`)) return;
    try {
      await api.delete(`/promo-codes/${c.id}`);
      toast.success("Codice eliminato");
      await load();
    } catch (e) { toast.error("Eliminazione non riuscita"); }
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="promo-codes-page">
      <div className="flex items-center justify-between gap-3 mb-8 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><Ticket style={{ width: 20, height: 20 }} className="text-primary" /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">Codici Promo</h1>
            <p className="text-sm text-slate-500">Crea codici sconto applicabili dai tuoi clienti nella vista del preventivo.</p>
          </div>
        </div>
        {isPro && <Button data-testid="promo-new-button" onClick={openNew} className="font-semibold"><Plus style={{ width: 16, height: 16 }} className="mr-1.5" /> Nuovo Codice</Button>}
      </div>

      {!isPro ? (
        <div data-testid="promo-pro-lock" className="rounded-2xl border-2 border-dashed border-amber-200 bg-amber-50/50 p-10 text-center max-w-xl mx-auto">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <Lock style={{ width: 26, height: 26 }} className="text-amber-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-900">I Codici Promo sono una funzione Pro</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">Passa al piano Pro o Fondatore per creare codici sconto personalizzati e aumentare le conversioni dei tuoi preventivi.</p>
          <Button data-testid="promo-upgrade-cta" onClick={() => navigate("/billing")} className="mt-5 font-semibold">
            <Crown style={{ width: 16, height: 16 }} className="mr-2" /> Scopri i piani Pro
          </Button>
        </div>
      ) : codes.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-card p-12 text-center">
          <p className="text-slate-400">Nessun codice promo. Creane uno per offrire sconti ai tuoi clienti.</p>
        </div>
      ) : (
        <div className="bg-card border border-slate-200/80 rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-border">
                <th className="px-5 py-3 font-semibold">Codice</th>
                <th className="px-5 py-3 font-semibold">Sconto</th>
                <th className="px-5 py-3 font-semibold">Scadenza</th>
                <th className="px-5 py-3 font-semibold">Utilizzi</th>
                <th className="px-5 py-3 font-semibold">Stato</th>
                <th className="px-5 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {codes.map((c) => (
                <tr key={c.id} data-testid={`promo-row-${c.code}`} className="hover:bg-slate-50/60">
                  <td className="px-5 py-3 font-mono font-bold text-slate-900">{c.code}</td>
                  <td className="px-5 py-3 text-slate-700">{c.discount_type === "percent" ? `${c.discount_value}%` : `€ ${c.discount_value}`}</td>
                  <td className="px-5 py-3 text-slate-500">{c.expires_at ? fmtDate(c.expires_at) : "—"}</td>
                  <td className="px-5 py-3 text-slate-500 font-mono">{c.uses_count}{c.max_uses != null ? ` / ${c.max_uses}` : ""}</td>
                  <td className="px-5 py-3">
                    {c.active
                      ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Attivo</Badge>
                      : <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200">Disattivato</Badge>}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" data-testid={`promo-edit-${c.code}`} onClick={() => openEdit(c)}><Pencil style={{ width: 15, height: 15 }} /></Button>
                      <Button size="sm" variant="ghost" data-testid={`promo-delete-${c.code}`} onClick={() => remove(c)} className="text-red-500 hover:text-red-600 hover:bg-red-50"><Trash2 style={{ width: 15, height: 15 }} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="promo-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifica Codice Promo" : "Nuovo Codice Promo"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Codice</label>
              <Input data-testid="promo-form-code" value={form.code} disabled={!!editing}
                onChange={(e) => setForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))}
                placeholder="Es. BENVENUTO10" className="font-mono uppercase mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo sconto</label>
                <Select value={form.discount_type} onValueChange={(v) => setForm((s) => ({ ...s, discount_type: v }))}>
                  <SelectTrigger data-testid="promo-form-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentuale (%)</SelectItem>
                    <SelectItem value="amount">Importo fisso (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Valore</label>
                <Input data-testid="promo-form-value" type="number" min="0" value={form.discount_value}
                  onChange={(e) => setForm((s) => ({ ...s, discount_value: e.target.value }))}
                  placeholder={form.discount_type === "percent" ? "10" : "50"} className="font-mono mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Scadenza (facolt.)</label>
                <Input data-testid="promo-form-expires" type="date" value={form.expires_at}
                  onChange={(e) => setForm((s) => ({ ...s, expires_at: e.target.value }))} className="mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Max utilizzi (facolt.)</label>
                <Input data-testid="promo-form-maxuses" type="number" min="0" value={form.max_uses}
                  onChange={(e) => setForm((s) => ({ ...s, max_uses: e.target.value }))}
                  placeholder="illimitati" className="font-mono mt-1.5" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <span className="text-sm font-medium text-slate-700">Codice attivo</span>
              <Switch data-testid="promo-form-active" checked={form.active} onCheckedChange={(v) => setForm((s) => ({ ...s, active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="promo-form-save" onClick={save} disabled={saving} className="font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
